import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Star, ShoppingCart, Heart, Truck, Shield, RefreshCw, Check, Package } from "lucide-react";
import { shopApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useCart } from "@/lib/store";
import ProductCard from "@/components/ProductCard";
import ProductMedia, { isPlayableVideoUrl } from "@/components/ProductMedia";
import ReviewsSection from "@/components/ReviewsSection";
import QASection from "@/components/QASection";
import RecentlyViewed, { pushRecentlyViewed } from "@/components/RecentlyViewed";
import { track } from "@/lib/pixels";
import { useSeo } from "@/lib/seo";
import DOMPurify from "dompurify";
import { toast } from "sonner";

export default function Product() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const { data, isLoading } = useQuery({ queryKey: ["product", slug], queryFn: () => shopApi.product(slug) });
  const p = data?.product;
  const bundles = data?.bundles || [];
  const fbt = data?.frequently_bought_together || [];
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState({});
  const [fbtSelected, setFbtSelected] = useState({});

  // Pre-check all FBT items on first load so users see the full bundle price
  React.useEffect(() => {
    if (fbt.length > 0 && Object.keys(fbtSelected).length === 0) {
      const initial = {};
      fbt.forEach((f) => { initial[f.product_id] = true; });
      setFbtSelected(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fbt.length]);

  // Track this product in the Recently Viewed history (localStorage)
  React.useEffect(() => {
    if (p?.product_id) {
      pushRecentlyViewed(p.product_id);
      track("ViewContent", {
        product_id: p.product_id,
        value: p.price,
        currency: "USD",
        items: [{ id: p.product_id, quantity: 1, price: p.price }],
      });
    }
  }, [p?.product_id]);

  // SEO / JSON-LD — hook must run before any early return, so guard with p?
  const canonical = p?.slug && typeof window !== "undefined" ? `${window.location.origin}/product/${p.slug}` : "";
  const shortDesc = (p?.description || "").replace(/\s+/g, " ").trim().slice(0, 155);
  // Truncate title to keep SEO title under Google's 60-char sweet spot.
  const seoTitleBase = p ? (p.title.length > 45 ? p.title.slice(0, 42).trim() + "…" : p.title) : "";
  useSeo(p ? {
    title: `${seoTitleBase} · NovalynxTech`,
    description: shortDesc || `Shop ${p.title} at NovalynxTech. Free shipping over $50, 12-month warranty, 30-day returns.`,
    canonical,
    image: p.images?.[0],
    ogType: "product",
    jsonLd: {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: p.title,
      description: shortDesc || `${p.title} at NovalynxTech`,
      image: (p.images || []).slice(0, 5),
      sku: p.sku,
      brand: { "@type": "Brand", name: p.brand || "NovalynxTech" },
      aggregateRating: p.reviews_count > 0 ? {
        "@type": "AggregateRating",
        ratingValue: p.rating || 4.5,
        reviewCount: p.reviews_count,
      } : undefined,
      offers: {
        "@type": "Offer",
        url: canonical,
        priceCurrency: "USD",
        price: p.price,
        availability: p.inventory > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      },
    },
  } : {});

  const off = p && p.compare_at_price && p.compare_at_price > p.price
    ? Math.round(((p.compare_at_price - p.price) / p.compare_at_price) * 100)
    : 0;
  // CJ often returns HTML in the description. Strip risky tags, keep formatting.
  // ALSO strip <img> tags because CJ embeds huge marketing images that duplicate
  // the gallery on the left — the user explicitly asked to remove them.
  const sanitizedDescription = React.useMemo(() => {
    const raw = p?.description || "";
    if (!/<[a-z][^>]*>/i.test(raw)) return { __html: null, plain: raw };
    // DOMPurify handles all XSS vectors (script, on-handlers, javascript:, SVG, data: URIs, etc.)
    // We also strip <img>/<picture>/<figure> since the gallery already shows all product images.
    const clean = DOMPurify.sanitize(raw, {
      FORBID_TAGS: ["script", "style", "iframe", "img", "picture", "figure", "object", "embed", "form", "input"],
      FORBID_ATTR: ["style", "srcset"],
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    })
      // Kill orphaned "Product Image:" / "Detail Photo:" headings that CJ leaves
      // behind after their <img> tags get stripped.
      .replace(/<(b|strong|h[1-6])[^>]*>\s*(product\s*image|detail\s*photo|product\s*display|packaging\s*photo)s?\s*:?\s*<\/\1>/gi, "")
      // Clean up empty paragraphs / trailing <br>
      .replace(/<p>\s*(<br\s*\/?>\s*)*<\/p>/gi, "")
      .replace(/(<br\s*\/?>\s*){3,}/gi, "<br/>")
      .trim();
    return { __html: clean, plain: null };
  }, [p?.description]);

  if (isLoading) return <div className="p-16 text-center text-muted-foreground">Loading…</div>;
  if (!p) return <div className="p-16 text-center">Not found.</div>;

  const buyNow = () => { add(p, qty, variant); };
  const buyBundle = (bundle) => {
    for (let i = 0; i < (bundle.min_qty || 2); i++) add(p, 1, variant);
    toast.success(`Bundle applied — ${bundle.discount_percent}% will be deducted at checkout`);
  };
  const addFbt = () => {
    add(p, 1, variant);
    fbt.filter((f) => fbtSelected[f.product_id]).forEach((f) => add(f, 1));
    toast.success("Bundle added to cart");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 md:py-8">
      {/* Breadcrumb — hidden on mobile (Temu/Amazon pattern: mobile goes straight to the hero image) */}
      <nav className="hidden md:flex text-xs text-muted-foreground mb-6 items-center gap-2 flex-wrap">
        <Link to="/">Home</Link> / <Link to={`/shop?category=${p.category}`}>{p.category}</Link> / <span className="text-white line-clamp-1">{p.title}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-6 md:gap-8 lg:gap-12">
        {/* Gallery — vertical thumb rail on desktop (Temu/Amazon style) */}
        <div className="min-w-0">
          {(() => {
            const hasVideo = isPlayableVideoUrl(p.video_url);
            const slots = hasVideo
              ? [{ type: "video" }, ...(p.images || []).map((im) => ({ type: "image", src: im }))]
              : (p.images || []).map((im) => ({ type: "image", src: im }));
            const active = slots[img] || slots[0] || { type: "image", src: p.images?.[0] };
            return (
              <div className="flex gap-3">
                {slots.length > 1 && (
                  <div className="hidden md:flex flex-col gap-2 w-16 shrink-0 max-h-[560px] overflow-y-auto scrollbar-thin" data-testid="gallery-thumb-rail">
                    {slots.map((s, i) => (
                      <button
                        key={`${s.type}-${s.src || i}`}
                        onClick={() => setImg(i)}
                        onMouseEnter={() => setImg(i)}
                        className={`relative aspect-square rounded-lg overflow-hidden border-2 shrink-0 transition ${i === img ? "border-primary" : "border-white/10 hover:border-white/30"}`}
                        data-testid={`gallery-thumb-${i}`}
                      >
                        {s.type === "video" ? (
                          <>
                            <img src={p.images?.[0]} alt="" className="w-full h-full object-cover opacity-80" />
                            <span className="absolute inset-0 flex items-center justify-center bg-black/40">
                              <span className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                                <svg className="w-3 h-3 text-black ml-0.5" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                              </span>
                            </span>
                          </>
                        ) : (
                          <img src={s.src} alt="" className="w-full h-full object-cover" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex-1 min-w-0 w-full">
                  <div
                    className="relative aspect-[4/3] md:aspect-square rounded-2xl overflow-hidden bg-card border border-white/5 md:sticky md:top-4 max-w-full touch-pan-y select-none"
                    onTouchStart={(e) => { window.__gTouchX = e.touches[0].clientX; window.__gTouchY = e.touches[0].clientY; }}
                    onTouchEnd={(e) => {
                      const dx = e.changedTouches[0].clientX - (window.__gTouchX || 0);
                      const dy = e.changedTouches[0].clientY - (window.__gTouchY || 0);
                      // Only trigger if horizontal swipe > 40px AND more horizontal than vertical
                      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) && slots.length > 1) {
                        if (dx < 0) setImg((i) => (i + 1) % slots.length);
                        else setImg((i) => (i - 1 + slots.length) % slots.length);
                      }
                    }}
                    data-testid="hero-swipe-area"
                  >
                    {active.type === "video" ? (
                      <ProductMedia videoUrl={p.video_url} poster={p.images?.[0]} alt={p.title} />
                    ) : (
                      <img src={active.src} alt={p.title} className="absolute inset-0 w-full h-full object-cover pointer-events-none" data-testid="product-hero-image" draggable="false" />
                    )}
                    {/* Mobile: image index dot indicator (Temu-style: "3 / 9") */}
                    {slots.length > 1 && (
                      <div className="md:hidden absolute bottom-3 right-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white text-xs font-medium tabular-nums pointer-events-none">
                        {img + 1} / {slots.length}
                      </div>
                    )}
                    {/* Mobile: page-dot indicators along the bottom center */}
                    {slots.length > 1 && slots.length <= 12 && (
                      <div className="md:hidden absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none">
                        {slots.map((_, i) => (
                          <span key={i} className={`h-1.5 rounded-full transition-all ${i === img ? "w-4 bg-white" : "w-1.5 bg-white/40"}`} />
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Mobile: horizontal scrolling thumb rail (Temu/Amazon pattern) */}
                  {slots.length > 1 && (
                    <div className="md:hidden flex gap-2 mt-3 overflow-x-auto -mx-4 px-4 scrollbar-thin snap-x snap-mandatory" data-testid="mobile-thumb-rail">
                      {slots.map((s, i) => (
                        <button
                          key={`m-${s.type}-${s.src || i}`}
                          onClick={() => setImg(i)}
                          className={`relative w-14 h-14 shrink-0 rounded-lg overflow-hidden border-2 snap-start ${i === img ? "border-primary" : "border-white/10"}`}
                          data-testid={`m-gallery-thumb-${i}`}
                        >
                          {s.type === "video" ? (
                            <>
                              <img src={p.images?.[0]} alt="" className="w-full h-full object-cover opacity-70" />
                              <span className="absolute inset-0 flex items-center justify-center">
                                <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                              </span>
                            </>
                          ) : (
                            <img src={s.src} alt="" className="w-full h-full object-cover" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Details */}
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-widest text-primary mb-2">{p.brand}</p>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mb-3 break-words" data-testid="product-title">{p.title}</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4 flex-wrap">
            {(p.reviews_count > 0) ? (
              <>
                <Star className="h-4 w-4 text-primary" fill="currentColor" />
                <span className="text-white">{p.rating?.toFixed(1)}</span>
                <span>·</span>
                <a href="#reviews" onClick={(e) => { e.preventDefault(); document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" }); }} className="hover:text-white underline-offset-2 hover:underline">
                  {p.reviews_count} reviews
                </a>
              </>
            ) : (
              <a href="#reviews" onClick={(e) => { e.preventDefault(); document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth" }); }} className="hover:text-white underline-offset-2 hover:underline">
                Be the first to review
              </a>
            )}
            <span className="hidden md:inline">· SKU {p.sku}</span>
          </div>
          <div className="flex items-baseline gap-3 mb-6 flex-wrap">
            <span className="text-3xl md:text-4xl font-bold text-primary" data-testid="product-price">${p.price.toFixed(2)}</span>
            {p.compare_at_price && p.compare_at_price > p.price && <>
              <span className="text-lg text-muted-foreground line-through">${p.compare_at_price.toFixed(2)}</span>
              <span className="chip bg-primary/20 text-primary border-primary/30">−{off}%</span>
            </>}
          </div>
          {/* Trust + urgency callouts (highest-ROI conversion boosters for cold ad traffic) */}
          <div className="flex flex-wrap gap-2 mb-4 text-xs" data-testid="conversion-callouts">
            {p.price < 50 && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">
                <Truck className="h-3.5 w-3.5" /> Add ${(50 - p.price).toFixed(2)} more for FREE shipping
              </span>
            )}
            {p.price >= 50 && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">
                <Truck className="h-3.5 w-3.5" /> FREE shipping
              </span>
            )}
            {(p.inventory ?? 999) < 20 && (p.inventory ?? 999) > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/25 animate-pulse">
                🔥 Only {p.inventory} left in stock
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 text-muted-foreground border border-white/10">
              ⚡ Ships in 24h
            </span>
          </div>
          <CollapsibleDescription
            html={sanitizedDescription.__html}
            plain={sanitizedDescription.plain}
          />

          {/* Variants — supports 3 shapes:
              1) Grouped: [{name:"Color", options:["Black","White"]}]
              2) CJ flat with images: [{name, image, sku, cost}]
              3) CJ malformed: [{name:"<full title> Black", options:[]}, ...]  ← auto-normalized below */}
          {p.variants?.length > 0 && (() => {
            // Detect CJ flat variants: each entry has an `image` or `sku` field.
            const isFlat = p.variants[0] && ("image" in p.variants[0] || "sku" in p.variants[0]);
            if (isFlat) {
              const selectedName = variant["_variant"] || "";
              return (
                <div className="mb-4">
                  <p className="text-sm mb-2">Variant: <span className="text-muted-foreground">{selectedName || "Select"}</span></p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {p.variants.map((v, i) => (
                      <button
                        key={v.sku || i}
                        onClick={() => setVariant({ _variant: v.name, sku: v.sku })}
                        className={`rounded-xl overflow-hidden border-2 transition text-left ${selectedName === v.name ? "border-primary" : "border-white/10 hover:border-white/30"}`}
                        data-testid={`variant-swatch-${i}`}
                        title={v.name}
                      >
                        {v.image ? (
                          <img src={v.image} alt={v.name} className="w-full aspect-square object-cover" />
                        ) : (
                          <div className="w-full aspect-square bg-secondary/40" />
                        )}
                        <p className="text-xs px-2 py-1.5 truncate">{v.name}</p>
                      </button>
                    ))}
                  </div>
                </div>
              );
            }
            // CJ malformed detection: every entry has empty options AND names share a common prefix.
            // Auto-normalize into a single "Options" group with short labels.
            const allEmptyOptions = p.variants.every((v) => !v.options || v.options.length === 0);
            if (allEmptyOptions && p.variants.length > 1) {
              // Find longest shared prefix among variant names.
              const names = p.variants.map((v) => v.name || "");
              let prefix = names[0];
              for (const n of names) {
                let i = 0;
                while (i < prefix.length && i < n.length && prefix[i] === n[i]) i++;
                prefix = prefix.slice(0, i);
              }
              // Trim to last word boundary so we don't break mid-word.
              prefix = prefix.replace(/\s+\S*$/, "").trim();
              const short = (n) => (prefix && n.startsWith(prefix) ? n.slice(prefix.length).trim() : n).replace(/^[-–—,: ]+/, "") || n;
              const selected = variant["Option"] || "";
              return (
                <div className="mb-4">
                  <p className="text-sm mb-2">Option: <span className="text-muted-foreground">{short(selected) || "Select"}</span></p>
                  <div className="flex flex-wrap gap-2">
                    {p.variants.map((v, i) => {
                      const label = short(v.name);
                      return (
                        <button
                          key={v.name || i}
                          onClick={() => setVariant({ Option: v.name })}
                          className={`px-4 py-2 rounded-lg text-sm border ${selected === v.name ? "border-primary bg-primary/10 text-primary" : "border-white/15 hover:border-white/30"}`}
                          data-testid={`variant-option-${i}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            }
            return p.variants.map((v) => (
              <div key={v.name} className="mb-4">
                <p className="text-sm mb-2 break-words">{v.name}: <span className="text-muted-foreground">{variant[v.name] || "Select"}</span></p>
                <div className="flex flex-wrap gap-2">
                  {(v.options || []).map((o) => (
                    <button key={o} onClick={() => setVariant({ ...variant, [v.name]: o })}
                      className={`px-4 py-2 rounded-lg text-sm border ${variant[v.name] === o ? "border-primary bg-primary/10 text-primary" : "border-white/15 hover:border-white/30"}`}
                      data-testid={`variant-${v.name}-${o}`}>{o}</button>
                  ))}
                </div>
              </div>
            ));
          })()}

          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center border border-white/15 rounded-lg">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="h-11 w-11">−</button>
              <span className="w-10 text-center" data-testid="product-qty">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="h-11 w-11">+</button>
            </div>
            <Button className="btn-primary h-11 flex-1" onClick={buyNow} data-testid="product-add-to-cart"><ShoppingCart className="h-4 w-4 mr-2" /> Add to cart</Button>
            <Button variant="outline" className="h-11 border-white/15" data-testid="product-wishlist"><Heart className="h-4 w-4" /></Button>
          </div>

          {/* Bundle offers */}
          {bundles.length > 0 && (
            <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 mb-4" data-testid="bundle-offers">
              <p className="text-sm font-semibold mb-2 flex items-center gap-2"><Package className="h-4 w-4 text-primary" /> Bundle & save</p>
              {bundles.map((b) => (
                <div key={b.bundle_id} className="flex items-center justify-between py-2 border-t border-white/5 first:border-0">
                  <div>
                    <p className="text-sm">{b.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {b.bundle_price ? `Bundle price $${b.bundle_price.toFixed(2)}` : `Buy ${b.min_qty}, save ${b.discount_percent}%`}
                    </p>
                  </div>
                  {b.slug && b.kind === "custom" ? (
                    <Link to={`/bundle/${b.slug}`}><Button size="sm" variant="outline" className="border-primary/40 text-primary" data-testid={`view-bundle-${b.bundle_id}`}>View bundle</Button></Link>
                  ) : (
                    <Button size="sm" variant="outline" className="border-primary/40 text-primary" onClick={() => buyBundle(b)} data-testid={`apply-bundle-${b.bundle_id}`}>Apply</Button>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-3 gap-3 text-xs text-muted-foreground mt-6 pt-6 border-t border-white/5">
            <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-primary" /> Ships in 24h</div>
            <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-primary" /> 12-mo warranty</div>
            <div className="flex items-center gap-2"><RefreshCw className="h-4 w-4 text-primary" /> 30-day returns</div>
          </div>

          {/* Ask-a-question prompt — drives Q&A engagement (indexed by Google for long-tail SEO) */}
          <a
            href="#qa"
            onClick={(e) => { e.preventDefault(); const el = document.getElementById("qa"); if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); } }}
            className="mt-4 flex items-center gap-3 rounded-xl bg-secondary/40 hover:bg-secondary/60 border border-white/5 hover:border-primary/40 p-3 transition"
            data-testid="ask-question-prompt"
          >
            <span className="w-8 h-8 rounded-full bg-primary/20 text-primary text-lg flex items-center justify-center">?</span>
            <div className="flex-1">
              <p className="text-sm font-medium">Have a question about this product?</p>
              <p className="text-xs text-muted-foreground">Ask other shoppers — real answers usually within 24h.</p>
            </div>
            <span className="text-primary text-xs">Ask →</span>
          </a>
        </div>
      </div>

      {/* Frequently Bought Together */}
      {fbt.length > 0 && (() => {
        const selected = fbt.filter((f) => fbtSelected[f.product_id]);
        const totalItems = 1 + selected.length;
        const rawTotal = p.price + selected.reduce((s, f) => s + f.price, 0);
        // Match the real global bundle applied at checkout (min_qty + discount_percent)
        const globalBundle = bundles.find((b) => b.kind === "global" && b.active !== false);
        const bundleActive = globalBundle && totalItems >= (globalBundle.min_qty || 2);
        const bundlePct = bundleActive ? (globalBundle.discount_percent || 0) : 0;
        const finalTotal = rawTotal * (1 - bundlePct / 100);
        const saved = rawTotal - finalTotal;
        return (
          <section className="mt-16" data-testid="fbt-section">
            <div className="flex items-end justify-between mb-6">
              <div>
                <h2 className="font-display text-2xl font-bold">Goes well with</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {globalBundle
                    ? `Bundle ${globalBundle.min_qty || 2}+ items to save ${globalBundle.discount_percent}% at checkout — and unlock free shipping over $50.`
                    : "Bundle to unlock free shipping over $50."}
                </p>
              </div>
            </div>
            <div className="rounded-2xl bg-card border border-white/5 p-6">
              <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Anchor product */}
                  <div className="flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/5 p-3" data-testid="fbt-anchor">
                    <img src={p.images?.[0]} className="h-16 w-16 rounded-lg object-cover" alt="" />
                    <div className="text-sm">
                      <p className="font-medium line-clamp-1 max-w-[160px]">{p.title}</p>
                      <p className="text-xs text-primary font-semibold">This item · ${p.price.toFixed(2)}</p>
                    </div>
                  </div>
                  {fbt.map((f) => (
                    <React.Fragment key={f.product_id}>
                      <span className="text-2xl text-muted-foreground select-none">+</span>
                      <label
                        className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition ${fbtSelected[f.product_id] ? "border-primary/40 bg-primary/5" : "border-white/10 opacity-60 hover:opacity-90"}`}
                        data-testid={`fbt-item-${f.product_id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!fbtSelected[f.product_id]}
                          onChange={(e) => setFbtSelected({ ...fbtSelected, [f.product_id]: e.target.checked })}
                          className="accent-primary h-4 w-4"
                        />
                        <img src={f.images?.[0]} className="h-16 w-16 rounded-lg object-cover" alt="" />
                        <div className="text-sm">
                          <p className="line-clamp-1 max-w-[160px]">{f.title}</p>
                          <p className="text-xs text-muted-foreground">${f.price.toFixed(2)}</p>
                        </div>
                      </label>
                    </React.Fragment>
                  ))}
                </div>
                <div className="border-t md:border-t-0 md:border-l md:pl-6 md:ml-2 pt-4 md:pt-0 border-white/10 min-w-[220px]">
                  <p className="text-xs text-muted-foreground uppercase tracking-widest">Bundle total</p>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-display text-3xl font-bold" data-testid="fbt-total">${finalTotal.toFixed(2)}</span>
                    {saved > 0 && <span className="text-sm text-muted-foreground line-through">${rawTotal.toFixed(2)}</span>}
                  </div>
                  {saved > 0 && (
                    <p className="text-xs text-primary font-semibold mt-1" data-testid="fbt-savings">You save ${saved.toFixed(2)} at checkout</p>
                  )}
                  <Button className="btn-primary w-full mt-3" onClick={addFbt} data-testid="fbt-add-all">
                    Add {totalItems} item{totalItems === 1 ? "" : "s"} to cart
                  </Button>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      <section className="mt-16">
        <Tabs defaultValue="specs">
          <TabsList className="bg-card border border-white/5">
            <TabsTrigger value="specs" data-testid="tab-specs">Specifications</TabsTrigger>
            <TabsTrigger value="shipping" data-testid="tab-shipping">Shipping</TabsTrigger>
            <TabsTrigger value="upsells" data-testid="tab-upsells">You may also like</TabsTrigger>
          </TabsList>
          <TabsContent value="specs" className="pt-6">
            <div className="grid md:grid-cols-2 gap-3 max-w-2xl">
              {Object.entries(p.specs || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-white/5 py-2 text-sm">
                  <span className="text-muted-foreground">{k}</span><span>{v}</span>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="shipping" className="pt-6 text-sm text-muted-foreground max-w-2xl">
            <p>{p.shipping_info || "Free standard shipping over $50. Express and overnight available."}</p>
            <ul className="mt-4 space-y-2">
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Standard: 5-7 business days ($4.99)</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Express: 2-3 business days ($12.99)</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> Overnight: next day ($24.99)</li>
            </ul>
          </TabsContent>
          <TabsContent value="upsells" className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {fbt.map((f) => <ProductCard key={f.product_id} product={f} />)}
            </div>
          </TabsContent>
        </Tabs>
      </section>

      <ReviewsSection product={p} />
      <div id="qa" />
      <QASection product={p} />
      <RecentlyViewed excludeId={p.product_id} title="You recently viewed" />

      {/* Mobile sticky Add-to-cart bar — appears after the shopper scrolls past the main CTA */}
      <MobileStickyBuyBar product={p} onAdd={buyNow} onBuyNow={() => { buyNow(); setTimeout(() => nav("/checkout"), 100); }} />

      {/* Bottom spacer so the sticky bar never covers content on mobile */}
      <div className="h-24 md:hidden" aria-hidden="true" />
    </div>
  );
}

// Collapsible description — shows a short preview on mobile with "Show more" toggle
// so the product image + title + price + CTA all fit above the fold (Amazon pattern).
function CollapsibleDescription({ html, plain }) {
  const [expanded, setExpanded] = React.useState(false);
  const contentEmpty = !html && !plain;
  if (contentEmpty) return null;
  return (
    <div className="mb-6" data-testid="product-description-wrap">
      <div
        className={`relative overflow-hidden transition-[max-height] duration-300 ${expanded ? "max-h-[6000px]" : "max-h-[120px] md:max-h-[none]"}`}
        data-testid="product-description-body"
      >
        {html ? (
          <div
            className="text-muted-foreground leading-relaxed prose prose-invert prose-sm max-w-none break-words [&_p]:mb-2 [&_b]:text-white [&_strong]:text-white [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_img]:rounded-lg [&_img]:my-3 [&_*]:max-w-full"
            dangerouslySetInnerHTML={{ __html: html }}
            data-testid="product-description-html"
          />
        ) : (
          <p className="text-muted-foreground leading-relaxed whitespace-pre-line break-words">{plain}</p>
        )}
        {/* Gradient fade at the bottom when collapsed */}
        {!expanded && (
          <div className="md:hidden absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-background to-transparent pointer-events-none" />
        )}
      </div>
      <button
        onClick={() => setExpanded((v) => !v)}
        className="md:hidden mt-2 text-sm text-primary font-medium hover:underline"
        data-testid="toggle-description"
      >
        {expanded ? "Show less" : "Show more"}
      </button>
    </div>
  );
}


function MobileStickyBuyBar({ product, onAdd, onBuyNow }) {
  const [visible, setVisible] = React.useState(false);
  React.useEffect(() => {
    // Show once the main "Add to cart" button scrolls out of view
    const anchor = document.querySelector('[data-testid="product-add-to-cart"]');
    if (!anchor) { setVisible(true); return; }
    const io = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0, rootMargin: "-40px 0px 0px 0px" }
    );
    io.observe(anchor);
    return () => io.disconnect();
  }, []);

  if (!product) return null;
  return (
    <div
      className={`md:hidden fixed bottom-0 inset-x-0 z-50 glass border-t border-white/10 transition-transform duration-300 ${visible ? "translate-y-0" : "translate-y-full"}`}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="mobile-sticky-buy-bar"
    >
      {/* Two-button Temu/Amazon-style CTA — Add to Cart (secondary) + Buy Now (primary) */}
      <div className="flex items-stretch gap-2 px-3 py-2.5">
        <button
          onClick={onAdd}
          data-testid="sticky-add-to-cart"
          className="flex-1 h-12 rounded-full bg-primary/15 text-primary font-semibold text-sm border border-primary/40 flex items-center justify-center gap-2 active:scale-95 transition"
        >
          <ShoppingCart className="h-4 w-4" /> Add to cart
        </button>
        <button
          onClick={onBuyNow}
          data-testid="sticky-buy-now"
          className="flex-1 h-12 rounded-full bg-primary text-primary-foreground font-bold text-sm shadow-lg shadow-primary/30 flex items-center justify-center gap-2 active:scale-95 transition"
        >
          Buy now · ${product.price.toFixed(2)}
        </button>
      </div>
    </div>
  );
}
